import type { ComponentPropsWithRef, ReactNode } from 'react'

export interface FormControlContainerProps extends ComponentPropsWithRef<'div'> {
    children: ReactNode
}

export type FormControlProps = FormControlContainerProps

function FormControlContainer({
    children,
    className = '',
    ...divProps
}: FormControlContainerProps) {
    return (
        <div
            {...divProps}
            className={`py-1 ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export type FormControlLabelVisibility = 'visible' | 'hidden' | 'invisible'

export interface FormControlLabelProps extends ComponentPropsWithRef<'label'> {
    required?: boolean
    visibility?: FormControlLabelVisibility
}

function FormControlLabel({
    children,
    htmlFor,
    required = false,
    visibility = 'visible',
    className = '',
    ...labelProps
}: FormControlLabelProps) {
    if (visibility === 'hidden') {
        return null
    }

    return (
        <div className={visibility === 'invisible' ? 'invisible' : ''}>
            <label
                {...labelProps}
                htmlFor={htmlFor}
                className="text-sm text-gray-600 flex items-center"
            >
                {children}
                <span
                    aria-hidden="true"
                    className={
                        required
                            ? 'text-lg text-red-500'
                            : 'invisible text-lg text-red-500'
                    }
                >
                    *
                </span>
                {required && <span className="sr-only"> (required)</span>}
            </label>
        </div>
    )
}

export interface FormControlHelperTextProps extends ComponentPropsWithRef<'div'> {}

function FormControlHelperText({
    id,
    children,
    className = '',
    ...divProps
}: FormControlHelperTextProps) {
    if (!children) {
        return null
    }

    return (
        <div
            {...divProps}
            id={id}
            className={`mt-2 text-xs text-gray-500 ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export interface FormControlErrorProps extends ComponentPropsWithRef<'div'> {
    error?: string | null
}

function FormControlError({
    error,
    children,
    className = '',
    ...divProps
}: FormControlErrorProps) {
    const content = children ?? error

    if (!content) {
        return null
    }

    return (
        <div
            role="alert"
            {...divProps}
            className={className}
        >
            <small className="text-sm text-red-600">{content}</small>
        </div>
    )
}

export const FormControl = {
    Container: FormControlContainer,
    Label: FormControlLabel,
    HelperText: FormControlHelperText,
    Error: FormControlError,
}
